import { useEffect, useState } from "react";
import { aboutClient } from "../apiClient/aboutClient";
import { ApiError } from "../apiClient/apiRequest";
import type { AboutParagraph } from "../types/about.types";

function ParagraphRow({ paragraph, onChanged }: { paragraph: AboutParagraph; onChanged: () => void }) {
  const [text, setText] = useState(paragraph.text);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDirty = text.trim() !== paragraph.text;

  async function handleSave() {
    const trimmed = text.trim();
    if (!trimmed) {
      setError("Can’t save empty text — delete the paragraph instead.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await aboutClient.updateParagraph(paragraph.id, trimmed);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save this.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    setSubmitting(true);
    setError(null);
    try {
      await aboutClient.deleteParagraph(paragraph.id);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete this.");
      setSubmitting(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <li className="about-admin-row">
      <textarea
        className="about-admin-textarea"
        rows={5}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={submitting}
      />
      <div className="about-admin-row-actions">
        {confirmingDelete ? (
          <div className="cancel-confirm">
            <span>Delete?</span>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmingDelete(false)} disabled={submitting}>
              No
            </button>
            <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={submitting}>
              {submitting ? "Deleting…" : "Yes"}
            </button>
          </div>
        ) : (
          <>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={submitting || !isDirty}>
              {submitting ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn btn-danger-ghost" onClick={() => setConfirmingDelete(true)} disabled={submitting}>
              Delete
            </button>
          </>
        )}
      </div>
      {error && <span className="field-error">{error}</span>}
    </li>
  );
}

export function AboutAdminPage() {
  const [paragraphs, setParagraphs] = useState<AboutParagraph[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newText, setNewText] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  function refresh() {
    setIsLoading(true);
    setError(null);
    aboutClient
      .getParagraphs()
      .then(setParagraphs)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load content."))
      .finally(() => setIsLoading(false));
  }

  useEffect(refresh, []);

  async function handleAdd() {
    const trimmed = newText.trim();
    if (!trimmed) {
      setAddError("Write something first.");
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await aboutClient.addParagraph(trimmed);
      setNewText("");
      refresh();
    } catch (err) {
      setAddError(err instanceof ApiError ? err.message : "Couldn't add this.");
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Manage About content</h1>
          <p className="page-sub">Add or remove paragraphs shown on the About Madinet Masr page.</p>
        </div>
      </header>

      <div className="form-card">
        <h2>Add a paragraph</h2>
        <div className="form">
          <div className="form-field">
            <textarea rows={5} placeholder="New paragraph text…" value={newText} onChange={(e) => setNewText(e.target.value)} />
          </div>
          {addError && <div className="form-banner form-banner--error">{addError}</div>}
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={handleAdd} disabled={adding}>
              {adding ? "Adding…" : "Add paragraph"}
            </button>
          </div>
        </div>
      </div>

      <h2 className="admin-section-heading">Current paragraphs</h2>
      {error ? (
        <div className="state-panel">
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : paragraphs.length === 0 ? (
        <div className="state-panel">
          <p>No paragraphs yet — add the first one above.</p>
        </div>
      ) : (
        <ul className="about-admin-list">
          {paragraphs.map((p) => (
            <ParagraphRow key={p.id} paragraph={p} onChanged={refresh} />
          ))}
        </ul>
      )}
    </div>
  );
}
